(ns ductile.ui.scanner
  "UI components for scanning various identifiers for vehicles, utilizing different
  technologies like Google Cloud Vision OCR, JS scanner libraries and hardware scanners"
  (:require [applied-science.js-interop :as j]
            [clojure.edn :as edn]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.schema :as schema]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.scene :as scene]
            [kitchen-async.promise :as p]
            [malli.core :as m]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.log :as log]
            [reagent.core :as r]
            [shadow.esm :refer (dynamic-import)])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]))

(when (and (not (js-in "BarcodeDetector" js/window))
           (exists? js/location) ;; don't include it in clerk
           (not (str/starts-with? js/location.host  "dev.ductile.de:7799")) ; don't include it in portfolio
           (not (str/starts-with? js/location.host  "localhost:")))
  (-> (dynamic-import "./../barcode-detector-zbar/BarcodeDetectorPolyfill.min.js")
      (.then (fn [^js mod]
               (.setupPolyfill (.-default mod))))))

(def streaming?
  #{:streaming})

(def processing?
  #{:processing})

(def done?
  #{:result})

(defn pause-camera-stream! [!state video-el]
  (let [{:keys [scan-interval]} @!state]
    (when scan-interval
      (js/clearInterval scan-interval))
    (when (and video-el (.-srcObject video-el))
      (.. video-el -srcObject getVideoTracks (forEach #(.stop %))))))

(declare handle-barcode-detect)

(defn stream-from-camera! [!state]
  (let [{max-w :width max-h :height :keys [video-el scanning-mode]} @!state
        w (or max-w js/innerWidth)
        h (or max-h js/innerHeight)
        mobile? (js-in "ontouchstart" js/window)
        specs (if mobile?
                (if (< w h)
                  (j/lit
                   {:video {:height {:exact w} :width {:exact h} :facingMode "environment"}
                    :audio false})
                  (j/lit
                   {:video {:height {:exact h} :width {:exact w} :facingMode "environment"}
                    :audio false}))
                (j/lit
                 {:video {:height {:exact h} :width {:exact w} :facingMode "environment"}
                  :audio false}))]
    (-> (js/navigator.mediaDevices.getUserMedia specs)
        (.then (fn [stream]
                 (set! (.-srcObject video-el) stream)
                 (when (= scanning-mode :barcode)
                   (let [detector (js/BarcodeDetector. (clj->js {:formats ["qr_code" "code_39"]}))]
                     (swap! !state
                            assoc
                            :scan-interval
                            (js/setInterval
                             (fn []
                               (handle-barcode-detect !state detector video-el)
                               nil)
                             300)))))))))

(def scanner-keys-map
  {"de.ductile.app.scanner.data_string" :scan/result
   "de.ductile.app.scanner.label_type" :scan/type
   "de.ductile.app.scanner.source" :scan/source
   "de.ductile.app.scanner.scanner_identifier" :scan/scanner-identifer
   "de.ductile.app.location.latitude" :location/latitude
   "de.ductile.app.location.longitude" :location/longitude
   "de.ductile.app.location.timestamp" :location/timestamp
   "de.ductile.app.location.provider" :location/provider
   "de.ductile.app.location.accuracy" :location/accuracy
   "de.ductile.app.location.bearing" :location/bearing
   "de.ductile.app.location.bearing-accuracy-degrees" :location/bearing-accuracy-degrees
   "de.ductile.app.location.altitude" :location/altitude
   "de.ductile.app.location.speed" :location/speed
   "de.ductile.app.location.speed-accuracy-meters-per-second" :location/speed-accuracy-meters-per-second
   "de.ductile.app.location.vertical-accuracy-meters" :location/vertical-accuracy-meters
   "de.ductile.app.device.version" :device/version
   "de.ductile.app.device.version-code" :device/version-code
   "de.ductile.app.device.model" :device/model
   "de.ductile.app.device.gps-issues" :device/gps-issues
   "de.ductile.app.device.device" :device/device
   "de.ductile.app.device.hardware" :device/hardware
   "de.ductile.app.device.sdk-version" :device/sdk-version
   "de.ductile.app.device.release" :device/release
   "de.ductile.app.device.codename" :device/codename})

(defn group-scan-keys [scan-map]
  (reduce (fn [acc [k v]]
            (if (qualified-keyword? k)
              (assoc-in acc [(keyword "scan" (namespace k)) k] v)
              (do (js/console.warn (str "group-scan-keys needs to be qualified-keyword?, is: '" (pr-str k) "'"))
                  acc)))
          {}
          scan-map))

(comment
  (group-scan-keys (clojure.set/map-invert scanner-keys-map))
  (group-scan-keys (assoc (clojure.set/map-invert scanner-keys-map) "de.ductile.app.location.unmapped-key" "123")))

(defn process-scan-json [scan]
  (let [grouped-scan (-> scan
                         js->clj
                         (set/rename-keys scanner-keys-map)
                         group-scan-keys)]
    (-> (merge (:scan/scan grouped-scan)
               (select-keys grouped-scan [:scan/location :scan/device]))
        (update :scan/source keyword))))

(defn fetch-device-location!
  "Helper fn to return the current device location. Works only when
  running in the proper native android app shell."
  []
  (when (exists? js/Scanner)
    (try
      (process-scan-json (.parse js/JSON (.getDeviceLocation js/Scanner)))
      (catch js/Error e
        (log/error ::fetch-device-location! (ex-data e))
        nil))))

(defn save-parse-edn-scan [scan]
  (try
    (-> scan
        edn/read-string)
    (catch js/Error e
      (js/console.warn "Unable to parse EDN from scan:" e scan)
      {:error :scan.error/unable-to-classify-scan
       :scan scan
       :error-message (tr "Can not scan this code")})))

(defn classify-scan
  [scan]
  ;; NOTE like `ductile.workflow/classify-scan` but returning a regex map, not a re-pattern
  (or (cond
        (not (string? scan)) {:error :workflow.error/unable-to-classify-scan :scan scan}
        (str/starts-with? scan "{") (save-parse-edn-scan scan)
        (schema/full-vin? scan) {:vehicle/vin scan}
        (parse-uuid scan) {:ductile/id (parse-uuid scan)}
        (schema/partial-vin? scan) {:vehicle/vin {:ductile.vehicle-search/regex (str scan "$")}}
        (m/validate :ars/id scan) {:ars/id scan}
        (= (count scan) 10) {:customer/identifier scan})
      {:error :workflow.error/unable-to-classify-scan
       :scan scan
       :error-message (tr "Scan needs to be between 7 and 17 characters long (VIN) or 20 characters or longer (ARS-ID).")}))

(defn handle-barcode-detect [!state ^js detector video-el]
  (.catch (.then (.detect detector video-el)
                 (fn [barcodes]
                   (when-not (empty? barcodes)
                     ;; always use first barcode even if there are multiple detected
                     (let [{:keys [format rawValue]} (j/lookup (first barcodes))]
                       (swap! !state assoc :phase :result)
                       (deframe/scan! (merge
                                       (fetch-device-location!)
                                       {:scan/result rawValue
                                        :scan/classified (classify-scan rawValue)
                                        :scan/source :camera
                                        :scan/type format
                                        :scan/context (:scan/context @!state)})))
                     (deframe/close-scanner!)
                     (pause-camera-stream! !state video-el))))
          (fn [error] (js/console.log "Barcode error: " error))))

(defn perform-ocr! [!state b64img]
  (let [{:keys [on-vin-detected on-ocr-error]} @!state]
    (swap! !state assoc :phase :processing)
    (p/let [result (http/$lambda 'ductile.scanner/handle-perform-ocr
                                 (merge
                                  (fetch-device-location!)
                                  {:scan/context (:scan/context @!state)
                                   :image b64img}))]
      (if (and (:error result) on-ocr-error)
        (on-ocr-error !state result)
        (when on-vin-detected
          (on-vin-detected !state result))))))

(defn resize! [!state]
  (let [{:keys [container-el phase video-el]} @!state
        {:keys [width height]} (j/lookup (.getBoundingClientRect container-el))
        portrait? (< width height)
        landscape-width 400
        cutout-inset 30]
    (swap! !state assoc :viewfinder {:width width
                                     :height height
                                     :cutout-x (if portrait?
                                                 cutout-inset
                                                 (/ (- width landscape-width) 2))
                                     :cutout-y (if portrait? 120 40)
                                     :cutout-width (if portrait?
                                                     (- width (* 2 cutout-inset))
                                                     landscape-width)
                                     :cutout-height 80})
    (when (streaming? phase)
      (when (.-srcObject video-el)
        (pause-camera-stream! !state video-el))
      (js/requestAnimationFrame #(stream-from-camera! !state)))))

(defn scan-mode [default]
  (or @deframe/!scan-mode default))

(defn scanner-view [initial-state]
  (r/with-let [!state (r/atom (merge {:phase :streaming
                                      :switching-mode-enabled? true
                                      :upload-file-enabled? true
                                      :scanning-mode (scan-mode :vin)}
                                     initial-state))
               video-ref #(when % (swap! !state assoc :video-el %))
               mask-id (str "viewfinder-" (gensym))
               resize-fn #(resize! !state)
               ref-fn (fn [el]
                        (if el
                          (let [{:keys [viewfinder]} @!state]
                            (when-let [bar-el (js/document.querySelector ".command-bar")]
                              (js/requestAnimationFrame #(.. bar-el -classList (add "hidden"))))
                            (swap! !state assoc :container-el el)
                            (when-not viewfinder (resize! !state))
                            (js/addEventListener "resize" resize-fn))
                          (let [{:keys [video-el]} @!state]
                            (pause-camera-stream! !state video-el)
                            (js/removeEventListener "resize" resize-fn))))]
    (let [{:keys [capture-el input-el scanning-mode upload-file-enabled? switching-mode-enabled? phase video-el viewfinder] :as _state} @!state
          {:keys [width height cutout-x cutout-y cutout-width cutout-height]} viewfinder]
      [:<>
       [:meta {:content "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" :name "viewport"}]
       [:div.bg-gray-600.vin-scanner.fixed.left-0.top-0
        {:style {:width js/innerWidth :height js/innerHeight}
         :ref ref-fn}
        [:video.bg-black
         {:width js/innerWidth
          :height js/innerHeight
          :playsInline true
          :autoPlay true
          :style {:width js/innerWidth :height js/innerHeight}
          :ref video-ref}]
        (when (and viewfinder (not= :barcode scanning-mode))
          [:<>
           [:canvas.capture
            {:class (str "absolute left-0 top-0 " (when (streaming? phase) "hidden"))
             :ref #(when % (swap! !state assoc :capture-el %))}]
           (let [r 3]
             [:svg.absolute.top-0.left-0.view-finder
              {:width width :height height :viewBox (str "0 0 " width " " height) :style {:width width :height height}}
              [:mask {:id mask-id}
               [:rect {:fill "white" :x "0" :y "0" :width width :height height}]
               [:rect {:fill "black" :x cutout-x :y cutout-y :width cutout-width :height cutout-height :rx r :ry r}]]
              [:rect {:mask (str "url(#" mask-id ")") :fill "rgba(0,0,0,.7)" :width width :height height :x 0 :y 0}]])
           [:div.border-2.border-white.absolute.viewfinder-frame
            {:style {:border-radius 3 :border-top-left-radius 0
                     :left cutout-x :top cutout-y :width cutout-width :height cutout-height}}
            [:div.bg-white.text-black.uppercase.tracking-wide.absolute.font-bold
             {:style {:font-size 9 :padding "1px 3px 0px 3px" :border-top-left-radius 3 :border-top-right-radius 3
                      :top -15 :left -2}}
             "VIN"]]])
        (when-not (done? phase)
          (when-not (= :barcode scanning-mode)
            [:div.capture-button.bg-green-400.rounded-full.border-4.border-white.absolute.flex.items-center.justify-center
             {:on-click (fn [_event]
                          (let [capture-ctx (.getContext capture-el "2d")
                                cutout-el (js/document.createElement "canvas")
                                cutout-ctx (.getContext cutout-el "2d")
                                portrait? (< width height)
                                video-w (.-videoWidth video-el)
                                video-h (.-videoHeight video-el)]
                            (j/assoc! capture-el :width (.-videoWidth video-el) :height (.-videoHeight video-el))
                            (j/assoc! cutout-el :width cutout-width :height cutout-height)
                            (if portrait?
                              (.drawImage capture-ctx video-el 0 0 video-w video-h 0 0 width height)
                              (.drawImage capture-ctx video-el (/ (- width video-w) 2) 0 video-w video-h))
                            (.drawImage cutout-ctx capture-el cutout-x cutout-y cutout-width cutout-height 0 0 cutout-width cutout-height)
                            (perform-ocr! !state (.toDataURL cutout-el "image/jpeg"))
                            (pause-camera-stream! !state video-el)))}
             (when (processing? phase)
               [:svg.animate-spin.text-white
                {:xmlns "http://www.w3.org/2000/svg"
                 :fill "none" :viewBox "0 0 24 24" :width 60 :height 60}
                [:circle.opacity-25 {:cx "12" :cy "12" :r "10" :stroke "currentColor" :stroke-width "4"}]
                [:path.opacity-75 {:fill "currentColor"
                                   :d "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"}]])]))
        (when upload-file-enabled?
          [:<>
           (when (= :barcode scanning-mode)
             ;; hack to allow detecting the barcode of an uploaded image
             [:img {:id "barcodeImage" :class "hidden"}])
           [:input {:type :file :style {:display :none} :accept "image/*"
                    :ref #(when % (swap! !state assoc :input-el %))
                    :on-change (fn [_event]
                                 (let [file (first (.. input-el -files))
                                       reader (js/FileReader.)]
                                   (j/assoc!
                                    reader
                                    :onloadend
                                    (fn [_]
                                      (case scanning-mode
                                        :vin (perform-ocr! !state (.-result reader))
                                        :barcode (let [img-el (js/document.getElementById "barcodeImage")]
                                                   (j/assoc!
                                                    img-el
                                                    :onload
                                                    (fn []
                                                      (handle-barcode-detect
                                                       !state
                                                       (js/BarcodeDetector. (clj->js {:formats ["qr_code" "code_39"]}))
                                                       img-el)
                                                      (deframe/close-scanner!)))
                                                   (set! (.-src img-el) (.-result reader))))
                                      (pause-camera-stream! !state video-el)
                                      (j/assoc! input-el :value "")))
                                   (when file
                                     (.readAsDataURL reader file))))}]
           [:button.text-white.absolute
            {:data-testid "filechooser-button"
             :on-click #(.click input-el)
             :style {:bottom 15 :left 15}}
            [:svg {:xmlns "http://www.w3.org/2000/svg"
                   :fill "none" :viewBox "0 0 24 24" :stroke "currentColor" :width 30 :height 30}
             [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2"
                     :d "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"}]]]])
        (when switching-mode-enabled?
          [:button.text-white.absolute
           {:disabled (not (streaming? phase))
            :on-click (fn []
                        (let [new-scanning-mode (if (= scanning-mode :barcode) :vin :barcode)]
                          (swap! !state assoc :scanning-mode new-scanning-mode)
                          (deframe/set-scan-mode! new-scanning-mode))
                        (resize! !state))
            :style {:bottom 19 :right 15 :opacity (when-not (streaming? phase) 0.5)}}
           (if (= scanning-mode :barcode)
             icons/barcode-scanner
             icons/vin-scanner)])]])))

(defn upload-image! [!state b64img]
  (let [{:keys [on-success on-error] :as state} @!state]
    (swap! !state assoc :phase :processing)
    (p/let [result (http/$lambda 'ductile.damages/handle-add-photo-to-entity
                                 {:entity-id (-> state :entity :ductile/id)
                                  :image b64img})]
      (if (and (:error result) on-error)
        (on-error !state result)
        (on-success !state result)))))

(defn photo-view [initial-state]
  (r/with-let [!state (r/atom (merge {:phase :streaming}
                                     initial-state))
               video-ref #(when % (swap! !state assoc :video-el %))
               ref-fn (fn [el]
                        (if el
                          (do (when-let [bar-el (js/document.querySelector ".command-bar")]
                                (js/requestAnimationFrame #(.. bar-el -classList (add "hidden"))))
                              (swap! !state assoc :container-el el)
                              (resize! !state))
                          (let [{:keys [video-el]} @!state]
                            (pause-camera-stream! !state video-el))))]
    (let [{:keys [capture-el input-el phase video-el] :as _state} @!state]
      [:<>
       [:meta {:content "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" :name "viewport"}]
       [:div.bg-gray-600.vin-scanner.fixed.left-0.top-0
        {:style {:width js/innerWidth :height js/innerHeight}
         :ref ref-fn}
        [:video.bg-black
         {:width js/innerWidth
          :height js/innerHeight
          :playsInline true
          :autoPlay true
          :style {:width js/innerWidth :height js/innerHeight}
          :ref video-ref}]
        [:canvas.capture
         {:class (str "absolute left-0 top-0 " (when (streaming? phase) "hidden"))
          :ref #(when % (swap! !state assoc :capture-el %))}]
        (when-not (done? phase)
          [:div.capture-button.bg-green-400.rounded-full.border-4.border-white.absolute.flex.items-center.justify-center
           {:on-click (fn [_event]
                        (let [capture-ctx (.getContext capture-el "2d")
                              video-w (.-videoWidth video-el)
                              video-h (.-videoHeight video-el)]
                          (j/assoc! capture-el :width video-w :height video-h)
                          (.drawImage capture-ctx video-el 0 0 video-w video-h 0 0 video-w video-h)
                          (upload-image! !state (.toDataURL capture-el "image/jpeg"))
                          (pause-camera-stream! !state video-el)))}
           (when (processing? phase)
             [:svg.animate-spin.text-white
              {:xmlns "http://www.w3.org/2000/svg"
               :fill "none" :viewBox "0 0 24 24" :width 60 :height 60}
              [:circle.opacity-25 {:cx "12" :cy "12" :r "10" :stroke "currentColor" :stroke-width "4"}]
              [:path.opacity-75 {:fill "currentColor"
                                 :d "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"}]])])
        [:input {:type :file :style {:display :none} :accept "image/*"
                 :ref #(when % (swap! !state assoc :input-el %))
                 :on-change (fn [_event]
                              (let [file (first (.. input-el -files))
                                    reader (js/FileReader.)]
                                (j/assoc! reader :onloadend (fn [_]
                                                              (upload-image! !state (.-result reader))
                                                              (pause-camera-stream! !state video-el)
                                                              (j/assoc! input-el :value "")))
                                (when file
                                  (.readAsDataURL reader file))))}]
        ;; upload file
        [:button.text-white.absolute.cursor-pointer
         {:on-click #(.click input-el)
          :style {:bottom 15 :left 15}
          :data-testid "filechooser-button"}
         [:svg {:xmlns "http://www.w3.org/2000/svg"
                :fill "none" :viewBox "0 0 24 24" :stroke "currentColor" :width 30 :height 30}
          [:path {:stroke-linecap "round" :stroke-linejoin "round" :stroke-width "2"
                  :d "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"}]]]]])))

(defscene scanning-portrait
  (let [{:as state :keys [width height]} {:width 380 :height 640}]
    [:div.rounded-lg.overflow-hidden.shadow-lg.mx-auto
     {:style {:width width :height height}}
     [scanner-view state]]))

(defscene scanning-landscape
  (let [{:as state :keys [width height]} {:width 640 :height 380}]
    [:div.rounded-lg.overflow-hidden.shadow-lg.mx-auto
     {:style {:width width :height height}}
     [scanner-view state]]))

(defscene done-portrait
  [!state]
  (let [{:as state :keys [width height]} {:width 380 :height 640 :phase :result}]
    [:div.rounded-lg.overflow-hidden.shadow-lg.mx-auto
     {:style {:width width :height height}}
     [scanner-view state]]))

(defscene done-landscape
  (let [{:as state :keys [width height]} {:width 640 :height 380 :phase :result}]
    [:div.rounded-lg.overflow-hidden.shadow-lg.mx-auto
     {:style {:width width :height height}}
     [scanner-view state]]))

(def !scan-confirmation (atom nil))

(defn scan-confirmation-view []
  (let [!el (hooks/use-ref nil)
        anim-duration 500
        visible-duration 3500
        !result (hooks/use-state nil)]
    (hooks/use-effect (fn []
                        (add-watch !scan-confirmation :scan-result (fn [_ _ _ result]
                                                                     (reset! !result result)))))
    (hooks/use-effect (fn []
                        (if @!result
                          (js/requestAnimationFrame (fn []
                                                      (set! (.. ^js @!el -style -transform) nil)
                                                      (set! (.. ^js @!el -style -opacity) 100)
                                                      (js/setTimeout #(reset! !result nil) visible-duration)))
                          (do (set! (.. ^js @!el -style -transform) "translateY(-100%)") ; don't use classes, tailwind js would glitch
                              (set! (.. ^js @!el -style -opacity) 0))))
                      [!result])
    [:div.fixed.w-full.p-4.z-30.pointer-events-none.transition-all.ease-in-out
     {:class (str "duration-" anim-duration)
      :style {:translate-y "-100%" :opacity 0}
      :ref !el}
     [:div.rounded-full.bg-black.bg-opacity-70.text-white.font-sans.text-sm.px-6.py-3.shadow-lg.border-black.flex.items-center.gap-4
      [:div.text-white [ui/icon {:size 30} :scan]]
      [:div
       [:div (tr "Scan") ": " [:span.font-bold (:scan/result @!result)]]
       (when (:error (:scan/classified @!result))
         [:div.text-red-400 (or (:error-message (:scan/classified @!result))
                                (tr (:error (:scan/classified @!result))))])]]]))

(comment
  (def example-scan
    {"de.ductile.app.location.speed" 0
     "de.ductile.app.device.version-code" 209
     "de.ductile.app.location.longitude" 6.5248873
     "de.ductile.app.scanner.label_type" "qrcode"
     "de.ductile.app.location.latitude" 51.32360572
     "de.ductile.app.location.vertical-accuracy-meters" 6
     "de.ductile.app.device.hardware" "qcom"
     "de.ductile.app.device.codename" "REL"
     "de.ductile.app.device.sdk-version" 29
     "de.ductile.app.device.device" "TC26"
     "de.ductile.app.scanner.source" "scanner"
     "de.ductile.app.scanner.data_string" "OAKBZDG2000120001200012000484215"
     "de.ductile.app.location.accuracy" 3.2160000801086426
     "de.ductile.app.location.timestamp" 1742806906000
     "de.ductile.app.scanner.scanner_identifier" "internal_imager"
     "de.ductile.app.location.speed-accuracy-meters-per-second" 0.12727922201156616
     "de.ductile.app.device.release" "10"
     "de.ductile.app.device.model" "TC26"
     "de.ductile.app.location.altitude" 86.68121337890625
     "de.ductile.app.location.provider" "gps"
     "de.ductile.app.device.version" "1.0-1a21762"})

  (process-scan-json example-scan))

;; This function is fine to be unused, it's called by the Ductile app!
(defn ^:export register-scans
  "Exported for the use in the ductile-Android app to deliver scan results
  from the hardware scanner"
  [results]
  (log/debug ::register-scans results)
  (let [processed-results (mapv process-scan-json results)
        {:scan/keys [result] :as new-scan} (peek processed-results)
        classified ((or (:classify-scan @deframe/!scan-tracker)
                        classify-scan)
                    result)
        scan (cond-> (assoc new-scan
                            :db/id :scanner/scan
                            :scan/classified classified)
               (not (:error classified))
               (assoc :scan/attr (-> classified first key)))]
    (reset! !scan-confirmation scan)
    (deframe/scan! scan)
    nil))

(def simulate-scan-binding "⌘S")

(defn simulate-scan!
  ([s opts]
   (register-scans [(merge {"de.ductile.app.scanner.data_string" s
                            "de.ductile.app.scanner.source" :scanner}
                           opts)])
   (deframe/route-dissoc! ::simulate-modal-open?))
  ([s]
   (simulate-scan! s {})))

(defn random-ars-id
  "Generates a real-world random ars id, for usage in dev & test"
  []
  (let [ars-id-sample "0AKBZDF2000120001200012000152061"]
    (apply str (concat (take 10 ars-id-sample)
                       (take (- (count ars-id-sample) 10) (repeatedly #(rand-nth (range 10))))))))

(defn simulate! [attr]
  (case attr
    :vehicle/vin (simulate-scan! (apply str "DUCT4LE" (take 10 (repeatedly (fn [] (rand-nth (range 10)))))))
    :ars/id (simulate-scan! (random-ars-id))))

(defn simulate-scan-modal []
  (let [!ref (hooks/use-ref)
        open? @(deframe/!route-get ::simulate-modal-open? false)]
    (hooks/use-effect (fn [el]
                        (let [input-focused? (fn [] (= js/document.activeElement @!ref))
                              paste-handler (fn [event]
                                              (when (and open? (not (input-focused?)))
                                                (simulate-scan! (.getData ^js (or (.-clipboardData event) (.-clipboardData js/window)) "text"))))
                              keypress-handler (fn [event]
                                                 (let [simulate-value @(deframe/!get ::scanned-value)]
                                                   (cond (and (.-metaKey event) (= "s" (.-key event)))
                                                         (do (.preventDefault event)
                                                             (deframe/route-assoc! ::simulate-modal-open? true))

                                                         (and open? (= "e" (.-key event)) (not (.-metaKey event)) (not (.-ctrlKey event)) (not (input-focused?)))
                                                         (do (.preventDefault event)
                                                             (.focus @!ref)
                                                             (.select @!ref))

                                                         (and open? (= "v" (.-key event)) (not (.-metaKey event)) (not (.-ctrlKey event)) (not (input-focused?)))
                                                         (do (.preventDefault event)
                                                             (simulate! :vehicle/vin))

                                                         (and open? (= "a" (.-key event)) (not (.-metaKey event)) (not (.-ctrlKey event)) (not (input-focused?)))
                                                         (do (.preventDefault event)
                                                             (simulate! :ars/id))

                                                         (and open? (seq simulate-value) (= "Enter" (.-key event)))
                                                         (simulate-scan! simulate-value))))]
                          (js/document.addEventListener "keydown" keypress-handler)
                          (js/document.addEventListener "paste" paste-handler)
                          #(do (js/document.removeEventListener "keydown" keypress-handler)
                               (js/document.removeEventListener "paste" paste-handler))))
                      [open? @!ref])
    [:div
     [ui/alert {:open? open?
                :on-close #(deframe/route-dissoc-in! [::simulate-modal-open?])}
      [ui/alert-content {:full? true
                         :class "max-h-[500px] w-[320px] md:w-[500px]"}
       [:div.relative {:class mobile/bg-page}
        [mobile/navbar {:title (tr "Simulate scan")
                        :back [:div]
                        :right-item [:button {:class "w-[24px] h-[24px] mt-[9px] flex items-center justify-center"
                                              :on-click #(deframe/route-dissoc! ::simulate-modal-open?)}
                                     [ui/icon {:size 20} :x]]}]
        [mobile/table {:title [:div.flex.gap-2.items-center (tr "Scanned Value") [ui/keys-hint "E"]]
                       :subtitle (tr "Or just paste to simulate scan")
                       :items [[mobile/list-item-input {:input {:placeholder (tr "Scanned Value")
                                                                :ref !ref
                                                                :default-value @(deframe/!get ::scanned-value)
                                                                :on-input (fn [^js e]
                                                                            (.stopPropagation e)
                                                                            (deframe/assoc! ::scanned-value (str/trim (.. e -target -value))))
                                                                :name nil}}]]}]
        [:div.p-3.pt-0.flex.flex-col.gap-3
         [mobile/button-outline
          {:on-click #(simulate! :vehicle/vin)}
          [:div.flex.gap-2.items-center
           (tr "Random VIN")
           [ui/keys-hint "V"]]]
         [mobile/button-outline
          {:on-click #(simulate! :ars/id)}
          [:div.flex.gap-2.items-center
           (tr "Random ARS-ID")
           [ui/keys-hint "A"]]]
         [mobile/button-outline
          {:on-click #(simulate-scan! "FZ-TANKEN" {"de.ductile.app.scanner.label_type" "qrcode"})}
          (tr "Gas Station")]]
        [:div.p-3.pt-0.flex.gap-3
         [mobile/button-full
          {:disabled (str/blank? @(deframe/!get ::scanned-value))
           :on-click #(simulate-scan! @(deframe/!get ::scanned-value))}
          [:div.flex.gap-2.items-center
           (tr "Simulate scan")
           [ui/keys-hint {:dark? true} "↩"]]]]]]]]))

;; JS console calls to simulate scanning
;; ductile.ui.scanner.register_scans([{"de.ductile.app.scanner.data_string": "somecode", "de.ductile.app.scanner.source": "scanner"}])
